import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, InputGroup, Alert } from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';

interface EditTransactionModalProps {
  show: boolean;
  onHide: () => void;
  transaction: any;
  onUpdate: () => void;
}

// Helper function to format date for input[type="date"]
const formatDateForInput = (dateValue: string | Date | null): string => {
  if (!dateValue) return '';
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    // Format as YYYY-MM-DD for HTML date input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
  show,
  onHide,
  transaction,
  onUpdate
}) => {
  const [formData, setFormData] = useState(() => {
    if (!transaction) {
      return {
        // Transaction basic info
        type: 'purchase',
        stage: 'pre_contract',
        agent_role: 'buyer_agent',
        client_name: '',
        client_email: '',
        
        // Property information
        property_address: '',
        property_city: '',
        property_state: '',
        property_zip: '',
        property_price: '',
        estimated_closing_date: '',
        
        // Property details for Smart Checklist
        property_type: 'residential',
        year_built: '',
        loan_type: 'conventional',
        has_hoa: false,
        has_pool: false,
        has_solar: false,
        solar_ownership: 'owned',
        septic_system: false,
        water_source: 'city',
        flood_zone: '',
        requires_flood_insurance: false,
        
        // Additional info
        notes: ''
      };
    }
    
    return {
      type: transaction.type || 'purchase',
      stage: transaction.stage || 'pre_contract',
      agent_role: transaction.agent_role || 'buyer_agent',
      client_name: transaction.client_name || '',
      client_email: transaction.client_email || '',
      
      // Extract property data
      property_address: transaction.property?.address || transaction.property_data?.address || '',
      property_city: transaction.property?.city || transaction.property_data?.city || '',
      property_state: transaction.property?.state || transaction.property_data?.state || '',
      property_zip: transaction.property?.zip || transaction.property_data?.zip || '',
      property_price: String(transaction.property?.price || transaction.property_data?.price || transaction.sale_price || ''),
      estimated_closing_date: formatDateForInput(transaction.estimated_closing_date || transaction.property_data?.estimated_closing_date || ''),
      
      // Property details for Smart Checklist
      property_type: transaction.property_data?.property_type || 'residential',
      year_built: transaction.property_data?.year_built || '',
      loan_type: transaction.property_data?.loan_type || 'conventional',
      has_hoa: transaction.property_data?.has_hoa || false,
      has_pool: transaction.property_data?.has_pool || false,
      has_solar: transaction.property_data?.has_solar || false,
      solar_ownership: transaction.property_data?.solar_ownership || 'owned',
      septic_system: transaction.property_data?.septic_system || false,
      water_source: transaction.property_data?.water_source || 'city',
      flood_zone: transaction.property_data?.flood_zone || '',
      requires_flood_insurance: transaction.property_data?.requires_flood_insurance || false,
      
      notes: transaction.notes || ''
    };
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [priceValue, setPriceValue] = useState('');

  // Format number with commas for display only
  const formatNumberWithCommas = (value: string) => {
    // Remove all non-numeric characters except decimal point
    const number = value.replace(/[^0-9.]/g, '');
    
    // Handle empty string
    if (!number) return '';
    
    // Split into integer and decimal parts
    const parts = number.split('.');
    
    // Format integer part with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Join back together (limit to 2 decimal places if decimal exists)
    if (parts.length > 1) {
      parts[1] = parts[1].substring(0, 2);
    }
    
    return parts.join('.');
  };

  // Parse formatted number to raw value
  const parseFormattedNumber = (value: string) => {
    return value.replace(/,/g, '');
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare the property_data JSON object with Smart Checklist fields
      const propertyData = {
        address: formData.property_address,
        city: formData.property_city,
        state: formData.property_state,
        zip: formData.property_zip,
        price: formData.property_price ? parseFloat(formData.property_price.replace(/,/g, '')) : null,
        estimated_closing_date: formData.estimated_closing_date,
        
        // Smart Checklist property details
        property_type: formData.property_type,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        loan_type: formData.loan_type,
        has_hoa: formData.has_hoa,
        has_pool: formData.has_pool,
        has_solar: formData.has_solar,
        solar_ownership: formData.solar_ownership,
        septic_system: formData.septic_system,
        water_source: formData.water_source,
        flood_zone: formData.flood_zone,
        requires_flood_insurance: formData.requires_flood_insurance
      };

      // Update transaction - only send fields that exist in the schema
      const updatePayload: any = {
        type: formData.type,
        stage: formData.stage,
        agent_role: formData.agent_role,
        client_name: formData.client_name,
        property_data: propertyData,
        notes: formData.notes
      };
      
      // Only add client_email if it has a value (since it's not in the schema)
      // This field might need to be added to the database schema later
      
      const response = await axiosInstance.put(`/transactions/${transaction.id}`, updatePayload);

      if (response.data.success) {
        toast.success('Transaction updated successfully!');
        onUpdate();
        onHide();
      }
    } catch (err: any) {
      console.error('Error updating transaction:', err);
      setError(err.response?.data?.error || 'Failed to update transaction');
      
      // If endpoint doesn't exist, show a different message
      if (err.response?.status === 404) {
        setError('Update endpoint not implemented yet. This feature is coming soon.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      // For all other inputs including dates
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Transaction</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          {/* Transaction Information */}
          <h6 className="mb-3">Transaction Information</h6>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Transaction Type</Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="purchase">Purchase</option>
                  <option value="listing">Listing</option>
                  <option value="rental">Rental</option>
                  <option value="referral">Referral</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Stage</Form.Label>
                <Form.Select
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  required
                >
                  <option value="pre_contract">Pre-Contract</option>
                  <option value="showing">Showing</option>
                  <option value="under_contract">Under Contract</option>
                  <option value="closing">Closing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Your Role</Form.Label>
                <Form.Select
                  name="agent_role"
                  value={formData.agent_role}
                  onChange={handleChange}
                  required
                >
                  <option value="buyer_agent">Buyer's Agent</option>
                  <option value="seller_agent">Seller's Agent</option>
                  <option value="dual_agent">Dual Agent</option>
                  <option value="listing_agent">Listing Agent</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Client Information */}
          <h6 className="mb-3 mt-4">Client Information</h6>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Client Name</Form.Label>
                <Form.Control
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  placeholder="Enter client name"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Client Email</Form.Label>
                <Form.Control
                  type="email"
                  name="client_email"
                  value={formData.client_email}
                  onChange={handleChange}
                  placeholder="Enter client email"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Property Information */}
          <h6 className="mb-3 mt-4">Property Information</h6>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Property Address</Form.Label>
                <Form.Control
                  type="text"
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleChange}
                  placeholder="Enter property address"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  name="property_city"
                  value={formData.property_city}
                  onChange={handleChange}
                  placeholder="Enter city"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  name="property_state"
                  value={formData.property_state}
                  onChange={handleChange}
                  placeholder="State"
                  maxLength={2}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>ZIP Code</Form.Label>
                <Form.Control
                  type="text"
                  name="property_zip"
                  value={formData.property_zip}
                  onChange={handleChange}
                  placeholder="ZIP"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Sale/List Price</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="property_price"
                    defaultValue={formData.property_price || ''}
                    onBlur={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        property_price: e.target.value
                      }));
                    }}
                    placeholder="0.00"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Estimated Closing Date</Form.Label>
                <Form.Control
                  type="date"
                  name="estimated_closing_date"
                  value={formData.estimated_closing_date}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Property Details for Smart Checklist */}
          <h6 className="mb-3 mt-4">Property Details (for Document Checklist)</h6>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Property Type</Form.Label>
                <Form.Select
                  name="property_type"
                  value={formData.property_type}
                  onChange={handleChange}
                  required
                >
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="land">Land</option>
                  <option value="mixed_use">Mixed Use</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Year Built</Form.Label>
                <Form.Control
                  type="number"
                  name="year_built"
                  value={formData.year_built}
                  onChange={handleChange}
                  placeholder="e.g. 2000"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Loan Type</Form.Label>
                <Form.Select
                  name="loan_type"
                  value={formData.loan_type}
                  onChange={handleChange}
                >
                  <option value="conventional">Conventional</option>
                  <option value="fha">FHA</option>
                  <option value="va">VA</option>
                  <option value="usda">USDA</option>
                  <option value="cash">Cash</option>
                  <option value="jumbo">Jumbo</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Property Features Row 1 */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="has_hoa"
                  label="Has HOA?"
                  checked={formData.has_hoa}
                  onChange={(e) => setFormData(prev => ({...prev, has_hoa: e.target.checked}))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="has_pool"
                  label="Has Pool?"
                  checked={formData.has_pool}
                  onChange={(e) => setFormData(prev => ({...prev, has_pool: e.target.checked}))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="has_solar"
                  label="Has Solar?"
                  checked={formData.has_solar}
                  onChange={(e) => setFormData(prev => ({...prev, has_solar: e.target.checked}))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="septic_system"
                  label="Septic System?"
                  checked={formData.septic_system}
                  onChange={(e) => setFormData(prev => ({...prev, septic_system: e.target.checked}))}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Conditional Solar Ownership Field */}
          {formData.has_solar && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Solar Ownership</Form.Label>
                  <Form.Select
                    name="solar_ownership"
                    value={formData.solar_ownership}
                    onChange={handleChange}
                  >
                    <option value="owned">Owned</option>
                    <option value="leased">Leased</option>
                    <option value="financed">Financed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Property Features Row 2 */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Water Source</Form.Label>
                <Form.Select
                  name="water_source"
                  value={formData.water_source}
                  onChange={handleChange}
                >
                  <option value="city">City Water</option>
                  <option value="well">Private Well</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Flood Zone</Form.Label>
                <Form.Control
                  type="text"
                  name="flood_zone"
                  value={formData.flood_zone}
                  onChange={handleChange}
                  placeholder="e.g. X, A, AE"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mt-4">
                <Form.Check
                  type="checkbox"
                  name="requires_flood_insurance"
                  label="Requires Flood Insurance?"
                  checked={formData.requires_flood_insurance}
                  onChange={(e) => setFormData(prev => ({...prev, requires_flood_insurance: e.target.checked}))}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Notes */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          <FaTimes className="me-2" />
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={loading}
        >
          <FaSave className="me-2" />
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditTransactionModal;